import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Package, Plus, Trash2, Pencil, Check, X, Eye, EyeOff, Layers, Save, BookmarkPlus, FolderOpen } from "lucide-react";
import { api, equivalencesApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 180 — Section "Matières du chantier" sur la fiche commande.
 *
 * - Liste toutes les order_materials avec produit/groupe, fournisseur, réf, qté, unité.
 * - Permet d'ajouter / éditer (qté, épaisseur, notes) / supprimer.
 * - Toggle "Afficher sur fiche" (show_on_worksheet) qui contrôle l'apparition sur le PDF.
 */
export default function OrderMaterialsSection({ order }) {
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  // Add form state
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    target_type: "product", // "product" | "group"
    product_id: "",
    equivalence_group_id: "",
    surface_m2: "",
    thickness_mm: "",
    notes: "",
    show_on_worksheet: true,
  });
  const [savingAdd, setSavingAdd] = useState(false);

  // Phase 181 — Material set templates
  const [templates, setTemplates] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [applyTplOpen, setApplyTplOpen] = useState(false);
  const [applyTplId, setApplyTplId] = useState("");
  const [applyingTpl, setApplyingTpl] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.listMaterialSetTemplates();
      setTemplates(r.data || []);
    } catch (e) { /* silent */ }
  }, []);

  const load = useCallback(async () => {
    if (!order?.id) return;
    try {
      setLoading(true);
      const [mats, prods, grps] = await Promise.all([
        api.getOrderMaterials(order.id),
        api.getAllProducts().catch(() => ({ data: [] })),
        equivalencesApi.listGroups().catch(() => ({ data: [] })),
      ]);
      setMaterials(Array.isArray(mats.data) ? mats.data : (mats.data?.materials || []));
      setProducts(prods.data || []);
      setGroups(grps.data || []);
    } catch (e) {
      toast.error("Erreur de chargement des matières");
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleSaveAsTemplate = async () => {
    const name = (saveTplName || "").trim();
    if (!name) { toast.error("Nom du modèle requis"); return; }
    try {
      setSavingTpl(true);
      const r = await api.createMaterialSetTemplate({ name, order_id: order.id });
      toast.success(`Modèle « ${r.data.name} » enregistré (${r.data.materials_count} matières)`);
      setSaveTplOpen(false); setSaveTplName("");
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement du modèle");
    } finally {
      setSavingTpl(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!applyTplId) { toast.error("Choisis un modèle"); return; }
    try {
      setApplyingTpl(true);
      const r = await api.applyMaterialSetTemplate(order.id, applyTplId);
      const skippedTxt = r.data.skipped > 0 ? ` · ${r.data.skipped} ignorées` : "";
      toast.success(`Modèle appliqué : ${r.data.applied} matières ajoutées${skippedTxt}`);
      setApplyTplOpen(false); setApplyTplId("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'application");
    } finally {
      setApplyingTpl(false);
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    if (!window.confirm(`Supprimer le modèle « ${tpl.name} » ?`)) return;
    try {
      await api.deleteMaterialSetTemplate(tpl.id);
      toast.success("Modèle supprimé");
      await loadTemplates();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const groupById = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);

  const toggleShowOnWorksheet = async (mat) => {
    const next = !(mat.show_on_worksheet !== false);
    try {
      // Optimistic update
      setMaterials((ms) => ms.map((m) => m.id === mat.id ? { ...m, show_on_worksheet: next } : m));
      await api.updateOrderMaterial(mat.id, { show_on_worksheet: next });
      toast.success(next ? "Visible sur la fiche" : "Masqué de la fiche");
    } catch (e) {
      // Revert
      setMaterials((ms) => ms.map((m) => m.id === mat.id ? { ...m, show_on_worksheet: !next } : m));
      toast.error("Erreur de mise à jour");
    }
  };

  const startEdit = (mat) => {
    setEditingId(mat.id);
    setEditDraft({
      surface_m2: mat.surface_m2 ?? "",
      thickness_mm: mat.thickness_mm ?? "",
      notes: mat.notes || "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async (mat) => {
    try {
      setAdding(true);
      const payload = {
        surface_m2: editDraft.surface_m2 !== "" ? parseFloat(editDraft.surface_m2) : null,
        thickness_mm: editDraft.thickness_mm !== "" ? parseFloat(editDraft.thickness_mm) : null,
        notes: editDraft.notes || null,
      };
      await api.updateOrderMaterial(mat.id, payload);
      toast.success("Matière mise à jour");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (mat) => {
    if (!window.confirm("Retirer cette matière de la commande ?")) return;
    try {
      await api.deleteOrderMaterial(mat.id);
      toast.success("Matière retirée");
      await load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const resetAddForm = () => {
    setAddForm({
      target_type: "product",
      product_id: "",
      equivalence_group_id: "",
      surface_m2: "",
      thickness_mm: "",
      notes: "",
      show_on_worksheet: true,
    });
  };

  const submitAdd = async (e) => {
    e?.preventDefault?.();
    if (addForm.target_type === "product" && !addForm.product_id) {
      toast.error("Choisis un produit");
      return;
    }
    if (addForm.target_type === "group" && !addForm.equivalence_group_id) {
      toast.error("Choisis un groupe d'équivalence");
      return;
    }
    if (!addForm.surface_m2 || parseFloat(addForm.surface_m2) <= 0) {
      toast.error("Quantité requise (> 0)");
      return;
    }
    try {
      setSavingAdd(true);
      const payload = {
        product_id: addForm.target_type === "product" ? addForm.product_id : null,
        equivalence_group_id: addForm.target_type === "group" ? addForm.equivalence_group_id : null,
        surface_m2: parseFloat(addForm.surface_m2),
        thickness_mm: addForm.thickness_mm !== "" ? parseFloat(addForm.thickness_mm) : null,
        total_cost: 0,
        notes: addForm.notes || null,
        show_on_worksheet: addForm.show_on_worksheet,
        allow_equivalence: true,
      };
      await api.lockOrderMaterial(order.id, payload);
      toast.success("Matière ajoutée");
      setAddOpen(false);
      resetAddForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSavingAdd(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Chargement des matières…
        </CardContent>
      </Card>
    );
  }

  const visibleCount = materials.filter((m) => m.show_on_worksheet !== false).length;

  return (
    <Card data-testid="order-materials-section">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4 text-teal-700" />
              Matières du chantier
              <Badge variant="secondary" className="text-[10px]">{materials.length}</Badge>
              {materials.length > 0 && (
                <Badge variant="outline" className="text-[10px] gap-1" title="Matières visibles sur la fiche PDF">
                  <Eye className="h-3 w-3" /> {visibleCount} sur fiche
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Vue détaillée des matières liées à cette commande (provenant de Matières &amp; estimations).
              Coche les matières que tu veux voir apparaître sur la fiche de chantier PDF.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm" variant="outline"
              onClick={() => setApplyTplOpen(true)}
              disabled={templates.length === 0}
              data-testid="apply-template-btn"
              title={templates.length === 0 ? "Aucun modèle enregistré pour l'instant" : "Appliquer un modèle existant"}
            >
              <FolderOpen className="h-4 w-4 mr-1" /> Appliquer un modèle ({templates.length})
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => { setSaveTplName(""); setSaveTplOpen(true); }}
              disabled={materials.length === 0}
              data-testid="save-template-btn"
              title={materials.length === 0 ? "Ajoute d'abord des matières" : "Enregistrer la liste actuelle comme modèle"}
            >
              <BookmarkPlus className="h-4 w-4 mr-1" /> Enregistrer comme modèle
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddOpen((v) => !v)}
              data-testid="add-material-toggle"
            >
              <Plus className="h-4 w-4 mr-1" />
              {addOpen ? "Annuler" : "Ajouter une matière"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add form */}
        {addOpen && (
          <form
            onSubmit={submitAdd}
            className="border rounded-md p-3 bg-slate-50 space-y-3"
            data-testid="add-material-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-3">
                <Label className="text-xs">Type</Label>
                <Select
                  value={addForm.target_type}
                  onValueChange={(v) => setAddForm({ ...addForm, target_type: v, product_id: "", equivalence_group_id: "" })}
                >
                  <SelectTrigger data-testid="add-material-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Produit fournisseur</SelectItem>
                    <SelectItem value="group">Groupe d&apos;équivalence</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-9">
                <Label className="text-xs">{addForm.target_type === "product" ? "Produit" : "Groupe d'équivalence"}</Label>
                {addForm.target_type === "product" ? (
                  <Select
                    value={addForm.product_id}
                    onValueChange={(v) => setAddForm({ ...addForm, product_id: v })}
                  >
                    <SelectTrigger data-testid="add-material-product-select">
                      <SelectValue placeholder="Choisir un produit…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`add-material-product-${p.id}`}>
                          {p.name} · {p.supplier_name || "—"} · {p.reference_unit || ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select
                    value={addForm.equivalence_group_id}
                    onValueChange={(v) => setAddForm({ ...addForm, equivalence_group_id: v })}
                  >
                    <SelectTrigger data-testid="add-material-group-select">
                      <SelectValue placeholder="Choisir un groupe…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {groups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name} · {g.reference_unit || ""} · {(g.product_ids || []).length} produit(s)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Quantité (m² ou unité ref) *</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={addForm.surface_m2}
                  onChange={(e) => setAddForm({ ...addForm, surface_m2: e.target.value })}
                  data-testid="add-material-quantity"
                  required
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">Épaisseur (mm)</Label>
                <Input
                  type="number" step="0.1" min="0"
                  value={addForm.thickness_mm}
                  onChange={(e) => setAddForm({ ...addForm, thickness_mm: e.target.value })}
                  data-testid="add-material-thickness"
                />
              </div>
              <div className="md:col-span-6">
                <Label className="text-xs">Notes</Label>
                <Input
                  value={addForm.notes}
                  onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                  placeholder="Optionnel"
                  data-testid="add-material-notes"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1 border-t">
              <label className="flex items-center gap-2 text-xs">
                <Switch
                  checked={addForm.show_on_worksheet}
                  onCheckedChange={(v) => setAddForm({ ...addForm, show_on_worksheet: v })}
                  data-testid="add-material-show-toggle"
                />
                Afficher sur la fiche de chantier (PDF)
              </label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => { setAddOpen(false); resetAddForm(); }} disabled={savingAdd}>
                  Annuler
                </Button>
                <Button type="submit" size="sm" disabled={savingAdd} data-testid="add-material-submit">
                  {savingAdd ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Ajouter la matière
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* List */}
        {materials.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4 text-center">
            Aucune matière liée à cette commande. Clique sur « Ajouter une matière » ou utilise la section « Matières (produits type) » ci-dessus.
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm" x-excluded="true">
              <thead className="bg-slate-100 text-xs uppercase" x-excluded="true">
                <tr x-excluded="true">
                  <th className="text-left px-3 py-2">Fournisseur</th>
                  <th className="text-left px-3 py-2">Désignation</th>
                  <th className="text-left px-3 py-2">Référence</th>
                  <th className="text-right px-3 py-2">Quantité</th>
                  <th className="text-left px-3 py-2">Unité</th>
                  <th className="text-center px-3 py-2" title="Affichée sur la fiche de chantier PDF">Sur fiche</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody x-excluded="true">
                {materials.map((m) => {
                  const isEditing = editingId === m.id;
                  const isVisible = m.show_on_worksheet !== false;
                  const prod = m.product_id ? productById[m.product_id] : null;
                  const grp = m.equivalence_group_id ? groupById[m.equivalence_group_id] : null;
                  const supplierName = m.supplier_name || prod?.supplier_name || (grp ? "Équivalence multi-fournisseurs" : "—");
                  const name = m.is_equivalence_group
                    ? `[Groupe] ${m.group_name || grp?.name || "—"}`
                    : (m.product_name || prod?.name || "—");
                  const reference = m.product_reference || prod?.reference || "—";
                  const qty = m.consumed_quantity ?? m.surface_m2;
                  const unit = m.reference_unit || prod?.reference_unit || grp?.reference_unit || "—";
                  return (
                    <tr
                      key={m.id}
                      data-testid={`material-row-${m.id}`}
                      x-excluded="true"
                      className={`border-t ${isVisible ? "" : "bg-slate-50/60 text-muted-foreground"}`}
                    >
                      <td className="px-3 py-2 text-xs">{supplierName}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {m.is_equivalence_group && <Layers className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
                          <span className="truncate">{name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{reference}</td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <Input
                            type="number" step="0.01"
                            value={editDraft.surface_m2}
                            onChange={(e) => setEditDraft({ ...editDraft, surface_m2: e.target.value })}
                            className="h-7 text-xs w-24 ml-auto"
                            data-testid={`material-edit-qty-${m.id}`}
                          />
                        ) : (
                          <span className="font-mono">{qty != null ? Number(qty).toFixed(2).replace(/\.?0+$/, "") : "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{unit}</td>
                      <td className="px-3 py-2 text-center">
                        <Switch
                          checked={isVisible}
                          onCheckedChange={() => toggleShowOnWorksheet(m)}
                          data-testid={`material-worksheet-toggle-${m.id}`}
                          aria-label="Afficher sur la fiche"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          {isEditing ? (
                            <>
                              <Button size="sm" variant="default" className="h-7 px-2" onClick={() => saveEdit(m)} disabled={adding} data-testid={`material-save-${m.id}`}>
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={cancelEdit} disabled={adding}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => startEdit(m)} title="Modifier" data-testid={`material-edit-${m.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => handleDelete(m)} title="Retirer" data-testid={`material-delete-${m.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Save as template dialog */}
      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>Enregistrer comme modèle</DialogTitle>
            <DialogDescription>
              Le modèle capturera les {materials.length} matière(s) de cette commande (produit/groupe + quantité + épaisseur + notes + visibilité sur fiche). Tu pourras le réappliquer sur une autre commande.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nom du modèle</Label>
              <Input
                value={saveTplName}
                onChange={(e) => setSaveTplName(e.target.value)}
                placeholder="Ex: ITE Maison 120m² laine de roche"
                data-testid="save-template-name"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplOpen(false)} disabled={savingTpl}>Annuler</Button>
            <Button
              onClick={handleSaveAsTemplate}
              disabled={savingTpl || !saveTplName.trim()}
              data-testid="save-template-confirm"
            >
              {savingTpl ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply template dialog */}
      <Dialog open={applyTplOpen} onOpenChange={setApplyTplOpen}>
        <DialogContent className="max-w-md" data-testid="apply-template-dialog">
          <DialogHeader>
            <DialogTitle>Appliquer un modèle de matières</DialogTitle>
            <DialogDescription>
              Les matières du modèle seront <strong>ajoutées</strong> à la commande actuelle (sans supprimer les existantes). Les produits/groupes hors périmètre seront ignorés.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Modèle à appliquer</Label>
              <Select value={applyTplId} onValueChange={setApplyTplId}>
                <SelectTrigger data-testid="apply-template-select">
                  <SelectValue placeholder="Choisir un modèle..." />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`apply-template-option-${t.id}`}>
                      {t.name} ({t.materials_count} matières)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {templates.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Gérer les modèles existants</summary>
                <div className="mt-2 space-y-1 max-h-48 overflow-auto" data-testid="template-list">
                  {templates.map((t) => (
                    <div key={t.id} className="flex items-center justify-between border rounded px-2 py-1.5" data-testid={`template-row-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{t.name}</div>
                        <div className="text-[10px] text-muted-foreground">{t.materials_count} matière(s) · créé par {t.created_by || "—"}</div>
                      </div>
                      <Button
                        size="sm" variant="ghost" className="h-7 px-2 text-red-600"
                        onClick={() => handleDeleteTemplate(t)}
                        data-testid={`template-delete-${t.id}`}
                        title="Supprimer ce modèle"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyTplOpen(false)} disabled={applyingTpl}>Annuler</Button>
            <Button
              onClick={handleApplyTemplate}
              disabled={applyingTpl || !applyTplId}
              data-testid="apply-template-confirm"
            >
              {applyingTpl ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
